import './styles.scss';
import React, {
    useState,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import InnerImageZoom from 'react-inner-image-zoom';
import { Divider, Stack } from '@mui/material';

import {
    Spinner,
    PlyViewer,
} from '../../..';

import StlViewer from '../../../StlViewer';

import { http } from '../../../../helpers';

const viewerStyle = {
    width: '100%',
    height: '100%',
};

function Renders({
    activeUrl,
    scans,
    viewMode,
    activeViewIndex,
    setActiveViewIndex,
    showTips,
    setShowTips,
}) {
    const [fetching, setFetching] = useState(false);
    const [scanBlob, setScanBlob] = useState(null);
    const [modelData, setModelData] = useState(null);

    const [rotationX, setRotationX] = useState(0);
    const [rotationY, setRotationY] = useState(0);
    const currentModelBuffer = modelData?.viewMode === viewMode
        && modelData?.activeUrl === activeUrl
        ? modelData.buffer
        : null;

    useEffect(() => {
        let active = true;

        if (!activeUrl) {
            setScanBlob(null);
            setModelData(null);
            setFetching(false);
        } else {
            setFetching(true);
            setScanBlob(null);
            setModelData(null);

            http.getBlob(activeUrl, 'application/sla')
                .then((response) => {
                    if (!active) return;

                    if (viewMode === 'stl' || viewMode === 'ply') {
                        setModelData({
                            activeUrl,
                            viewMode,
                            buffer: response.data,
                        });
                        setFetching(false);
                        return;
                    }

                    const image = btoa(
                        new Uint8Array(response.data).reduce(
                            (data, byte) => data + String.fromCharCode(byte),
                            '',
                        ),
                    );

                    const blob = `data:${response.headers[
                        'content-type'
                    ].toLowerCase()};base64,${image}`;

                    setScanBlob(blob);
                    setFetching(false);
                })
                .catch(() => {
                    if (!active) return;

                    console.warn('Failed to load scan');
                    setFetching(false);
                });
        }

        return () => {
            active = false;
        };
    }, [activeUrl, viewMode]);

    useEffect(() => {
        setRotationX(0);
        setRotationY(0);
    }, [activeUrl]);

    return (
        <div className="render-viewport">
            <div className="render-engine-wrapper">
                {fetching && (
                    <Spinner type="center" />
                )}

                {!fetching && currentModelBuffer && viewMode === 'stl' && (
                    <StlViewer
                        orbitControls
                        arrayBuffer={currentModelBuffer}
                        modelProps={{
                            rotationX,
                            rotationY,
                            scale: 1.2,
                        }}
                        style={viewerStyle}
                    />
                )}

                {!fetching && currentModelBuffer && viewMode === 'ply' && (
                    <div style={viewerStyle}>
                        <PlyViewer arrayBuffer={currentModelBuffer} source={activeUrl} />
                    </div>
                )}

                {!fetching && scanBlob && viewMode === 'thumbnail' && (
                    <div
                        style={{
                            ...viewerStyle,
                            display: 'flex',
                            justifyContent:
                                'center',
                            alignItems:
                                'center',
                            overflow: 'hidden',
                        }}
                    >
                        <InnerImageZoom
                            src={scanBlob}
                            zoomScale={3.5}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        />
                    </div>
                )}
            </div>

            <Stack className="render-position-selector" direction="row" divider={<Divider orientation="vertical" flexItem />}>
                {scans.map((s, i) => (
                    <button
                        key={s.id}
                        type="button"
                        className={`${i === activeViewIndex ? 'active' : ''} view`}
                        onClick={() => setActiveViewIndex(i)}
                    >
                        {s.displayLabel}
                    </button>
                ),
                )}
            </Stack>

            <div className="tips-container">
                <button
                    type="button"
                    className="tips-toggle"
                    onClick={() => setShowTips(!showTips)}
                >
                    <i className="material-icons">
                        tips_and_updates
                    </i>
                </button>

                {showTips && (viewMode === 'stl' || viewMode === 'ply') && (
                    <div className="tips-panel">
                        <h4>Controls</h4>
                        <ul>
                            <li>
                                Left click + drag
                                to rotate
                            </li>
                            <li>
                                Right click + drag
                                to pan
                            </li>
                            <li>
                                Scroll wheel to zoom
                            </li>
                            <li>
                                Ctrl + drag to move
                                image
                            </li>
                        </ul>
                    </div>
                )}
                {showTips && viewMode === 'thumbnail' && (
                    <div className="tips-panel">
                        <h4>Controls</h4>
                        <ul>
                            <li>
                                Left click
                                to toggle zoom
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

Renders.propTypes = {
    viewMode: PropTypes.oneOf([
        'thumbnail',
        'stl',
        'ply',
    ]).isRequired,
    activeUrl: PropTypes.string,
    activeViewIndex: PropTypes.number.isRequired,
    setActiveViewIndex:
        PropTypes.func.isRequired,
    showTips: PropTypes.bool.isRequired,
    setShowTips: PropTypes.func.isRequired,
    scans: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            dentalCaseId: PropTypes.string,
            label: PropTypes.string,
            displayLabel: PropTypes.string,
            displayIndex: PropTypes.number,
            stlUrl: PropTypes.string,
            plyUrl: PropTypes.string,
        }),
    ).isRequired,
};

Renders.defaultProps = {
    activeUrl: null,
};

export default Renders;
